'use client';
import React, { useRef } from 'react';
import styles from './MediaCarousel.module.css';

export interface MediaCardItem {
  title: string;
  url: string;
  thumb: string;
  meta?: string;
  kind: 'video' | 'pdf' | 'page';
}

const DEFAULT_THUMB = '/imgs/surface-waves/SURFACEWAVE/SURFACEWAVE.jpg';

export const MediaCarousel: React.FC<{ title: string; items: MediaCardItem[]; kind?: string }> = ({
  title,
  items,
}) => {
  const track = useRef<HTMLDivElement>(null);
  if (!items || items.length === 0) return null;

  const nudge = (dir: number) => {
    const el = track.current;
    if (el) el.scrollBy({ left: dir * Math.max(320, el.clientWidth * 0.8), behavior: 'smooth' });
  };

  return (
    <section className={styles.carousel} aria-label={title}>
      <div className={styles.head}>
        <h2 className={styles.title}>{title}</h2>
        <div className={styles.controls}>
          <button type="button" aria-label="Scroll left" className={styles.navBtn} onClick={() => nudge(-1)}>‹</button>
          <button type="button" aria-label="Scroll right" className={styles.navBtn} onClick={() => nudge(1)}>›</button>
        </div>
      </div>
      <div className={styles.track} ref={track}>
        {items.map((it, i) => (
          <a
            key={`${it.url}-${i}`}
            className={styles.card}
            href={it.url}
            target={it.kind === 'page' ? '_self' : '_blank'}
            rel="noopener noreferrer"
            title={it.title}
          >
            <div className={styles.thumbWrap}>
              <img
                className={styles.thumb}
                src={it.thumb || DEFAULT_THUMB}
                alt={it.title}
                loading="lazy"
                onError={(e) => {
                  const img = e.currentTarget;
                  if (img.dataset.fb === '1') { img.style.display = 'none'; return; }
                  img.dataset.fb = '1';
                  img.src = DEFAULT_THUMB;
                }}
              />
              <span className={`${styles.badge} ${styles[it.kind]}`}>
                {it.kind === 'video' ? '▶ video' : it.kind === 'pdf' ? 'PDF' : 'page'}
              </span>
              {it.meta ? <span className={styles.meta}>{it.meta}</span> : null}
            </div>
            <div className={styles.cardTitle}>{it.title}</div>
          </a>
        ))}
      </div>
    </section>
  );
};

export default MediaCarousel;
